import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cx } from '@/lib/cx';
import { isRealDate } from '@/components/catalog/form-utils';
import { Badge } from './Badge';
import { Button } from './Button';
import { Checkbox } from './Checkbox';
import { Field, Input } from './Input';
import { Modal } from './Modal';
import { RowCheckbox } from './RowCheckbox';
import { TH, type FrozenSide } from './Table';

/**
 * Where a column's filter actually acts.
 *
 * `'server'` — a real query param backs it, so selecting a value re-queries the
 * whole dataset. `'page'` — it can only ever touch the rows already loaded.
 * The distinction is not cosmetic: every list in this app is server-paginated,
 * so a value list built from 20 loaded rows that *looks* dataset-wide is a lie.
 * The popover states which one it is, every time.
 */
export type FilterScope = 'server' | 'page';
export type Emptiness = 'any' | 'empty' | 'notEmpty';
export type SortDirection = 'asc' | 'desc';

export interface ColumnFilterOption {
  value: string;
  label: string;
  /** Rows on the loaded page holding this value. Omitted where it would mislead. */
  count?: number;
}

/**
 * How a numeric column's two bounds are being used.
 *
 * ⚠️ **THE MEMBERS ARE NAMED FOR WHAT THE SERVER BOUNDS ACTUALLY DO, not for the
 * words a request for this control would naturally use.** Every one of these
 * params is INCLUSIVE — `amountMin=100` returns a document at exactly 100 — so
 * an operator called "greater than" over `amountMin` describes something the
 * query does not do, and the two disagree on exactly one row: the boundary,
 * which is the row somebody filtering on a threshold is most often looking for.
 * Calling it `'greaterThan'` and labelling it "At least" would move the lie off
 * the screen and into the identifier, where the next reader inherits it.
 *
 * ⛔ **DO NOT CLOSE THE GAP WITH A CLIENT-SIDE ADJUSTMENT.** A `+ 1` or a
 * `+ 0.01` to make a strict comparison out of an inclusive bound is arithmetic
 * on a money DECIMAL STRING — wrong at any scale but the one it assumed — and
 * it puts a figure on the wire that nobody typed.
 */
export type ColumnRangeOperator = 'atLeast' | 'atMost' | 'equals' | 'between';

/**
 * A numeric column's bounds, as TYPED TEXT.
 *
 * ⚠️ **Both halves are strings and neither is ever parsed to a number.** Money
 * bounds here are exact decimal strings (`"18000.00"`) and the wire wants them
 * verbatim; `Number()` on one is the float round-trip this codebase refuses
 * everywhere money is handled. The only reading either gets is a trim and a
 * shape check.
 *
 * `min` and `max` always mean the LOWER and the UPPER bound. The operator
 * decides which of them is rendered and which is sent — never what they mean:
 *
 * | operator  | rendered      | sent                                  |
 * | --------- | ------------- | ------------------------------------- |
 * | `atLeast` | `min`         | the min param                         |
 * | `atMost`  | `max`         | the max param                         |
 * | `equals`  | `min`         | BOTH params, both from `min`          |
 * | `between` | `min` + `max` | both                                  |
 *
 * ⚠️ **A bound the current operator does not render is not sent either**, so
 * what a person can see is exactly what went out. It is still KEPT, so moving
 * `atMost` → `between` → `atMost` does not destroy the number they typed.
 */
export interface ColumnRangeValue {
  operator: ColumnRangeOperator;
  min: string;
  max: string;
}

/**
 * What the panel needs in order to render a numeric column's bounds.
 *
 * The query-param names are deliberately NOT here: this component never builds
 * a request. `ColumnSpec.range` in `use-table-filters.ts` carries those.
 */
export interface ColumnRangeSpec {
  /**
   * May a bound be negative?
   *
   * ⛔ **PER COLUMN, AND NOT TO BE HARMONISED.** `pending` on
   * `/client-deliverables` is genuinely SIGNED — overdelivery is real, and the
   * source workbook carries rows at `-1` — so a floor of 0 there makes every
   * overdelivered row unreachable, which is the population somebody filtering
   * on "pending" is most likely hunting. `capacity`, `promised` and `completed`
   * are unsigned.
   *
   * Defaults to **false**, and that direction is chosen: an unsigned column
   * that forgets the flag refuses a negative in front of the person typing it,
   * where a signed column that forgot it would silently hide rows.
   */
  signed?: boolean;
  /**
   * May a bound carry a decimal point? Money can; a count cannot.
   *
   * Defaults to **false** for the mirror-image reason: a money column that
   * forgets it refuses `1000.50` visibly, where an integer column wrongly
   * allowing one sends a bound the API answers 422 to — a refusal that surfaces
   * as an emptied table somewhere else on the page.
   */
  decimal?: boolean;
  /**
   * Are the bounds calendar DATES (`YYYY-MM-DD`) rather than numbers?
   *
   * When true the boxes are `type="date"`, a bound is valid only if it is a
   * real calendar day (`2026-02-30` is refused here, as the server would), the
   * operators read "On or after" / "On or before" / "On" / "Between", and
   * `signed` and `decimal` are ignored. The bounds are still INCLUSIVE and
   * still sent as the exact string the box holds — never through a `Date`,
   * which would shift a day west of Greenwich.
   *
   * Defaults to **false**, so every numeric column behaves exactly as before.
   */
  date?: boolean;
}

/**
 * A number CONDITION's operator (owner 2026-10-29, ported from the LAE app's
 * productivity `ColumnFilterMenu` / `tableFilters.js`).
 *
 * ⚠️ **NOT `ColumnRangeOperator`, and the two must not be merged.** A range is
 * a pair of INCLUSIVE server bounds over exact decimal strings and can only say
 * "at least"; a condition is tested HERE, against a plain JS number the column's
 * `get` already returns, so it can be strict. Money never reaches this: a money
 * cell is a decimal string, and a condition on one would mean parsing it.
 */
export type ColumnNumberOperator = 'gt' | 'gte' | 'lt' | 'lte' | 'eq' | 'between';

/**
 * A number condition as TYPED TEXT, so a half-typed `-` or `1.` survives a
 * re-render and the remembered state is exactly what was on screen.
 *
 * `a` is the only box every operator renders; `between` adds `b`. Switching
 * operator never clears a box. Text is read by `resolveNumberCondition`, which
 * ignores spaces, thousands commas and one trailing `%` (as LAE does).
 */
export interface ColumnNumberCondition {
  operator: ColumnNumberOperator;
  a: string;
  b: string;
}

export interface ColumnFilterState {
  selected: string[];
  emptiness: Emptiness;
  /** Set only on a column that declared itself numeric — see `ColumnRangeValue`. */
  range?: ColumnRangeValue;
  /**
   * Set only on a `kind: 'number'` column — see `ColumnNumberCondition`. ANDed
   * with `selected` and `emptiness`; a null cell never matches it.
   */
  condition?: ColumnNumberCondition;
}

/** The operators, in the order they are offered. */
// oxlint-disable-next-line react/only-export-components
export const NUMBER_OPERATORS: readonly ColumnNumberOperator[] = ['gt', 'gte', 'lt', 'lte', 'eq', 'between'];

const NUMBER_OPERATOR_SYMBOLS: Record<ColumnNumberOperator, string> = {
  gt: '>',
  gte: '≥',
  lt: '<',
  lte: '≤',
  eq: '=',
  between: 'Between',
};

const NUMBER_OPERATOR_NAMES: Record<ColumnNumberOperator, string> = {
  gt: 'Greater than',
  gte: 'At least',
  lt: 'Less than',
  lte: 'At most',
  eq: 'Equals',
  between: 'Between',
};

/** The "nothing typed yet" condition, and the operator the editor opens on. */
// oxlint-disable-next-line react/only-export-components
export const EMPTY_CONDITION: ColumnNumberCondition = { operator: 'gt', a: '', b: '' };

const CONDITION_NUMBER = /^-?(?:\d+\.?\d*|\.\d+)$/;

/** One box: `null` when empty, `undefined` when unusable, else the number. */
function conditionBox(raw: string): number | null | undefined {
  const text = raw.replace(/[\s,]/g, '').replace(/%$/, '');
  if (text === '') return null;
  if (!CONDITION_NUMBER.test(text)) return undefined;
  const parsed = Number(text);
  return Number.isFinite(parsed) ? parsed : undefined;
}

/** What a condition actually tests, or `null` when it tests nothing. */
type ResolvedCondition =
  | { op: Exclude<ColumnNumberOperator, 'between'>; a: number }
  | { op: 'between'; a: number; b: number };

/**
 * The condition as a test, or `null` when it narrows nothing.
 *
 * A half-filled `between` is the one bound it has (≥ or ≤), as a half-filled
 * range is; an unusable box is ignored rather than matching nothing. A reversed
 * `between` is swapped, as LAE does.
 */
function resolveNumberCondition(condition: ColumnNumberCondition | undefined): ResolvedCondition | null {
  if (!condition) return null;
  const a = conditionBox(condition.a);
  if (condition.operator !== 'between') {
    return typeof a === 'number' ? { op: condition.operator, a } : null;
  }
  const b = conditionBox(condition.b);
  if (typeof a === 'number' && typeof b === 'number') {
    return a <= b ? { op: 'between', a, b } : { op: 'between', a: b, b: a };
  }
  if (typeof a === 'number') return { op: 'gte', a };
  if (typeof b === 'number') return { op: 'lte', a: b };
  return null;
}

/**
 * Does this cell pass the condition? No condition (or nothing usable typed)
 * passes everything. **Only a finite JS number can match**: null, undefined,
 * NaN and any string — a money decimal string included — never do.
 */
// oxlint-disable-next-line react/only-export-components
export function numberConditionMatches(
  condition: ColumnNumberCondition | undefined,
  cell: unknown,
): boolean {
  const test = resolveNumberCondition(condition);
  if (test === null) return true;
  if (typeof cell !== 'number' || !Number.isFinite(cell)) return false;
  switch (test.op) {
    case 'gt':
      return cell > test.a;
    case 'gte':
      return cell >= test.a;
    case 'lt':
      return cell < test.a;
    case 'lte':
      return cell <= test.a;
    case 'eq':
      return cell === test.a;
    case 'between':
      return cell >= test.a && cell <= test.b;
  }
}

function formatConditionNumber(value: number): string {
  return value.toLocaleString('en-GB', { maximumFractionDigits: 6 });
}

/**
 * The active-filter chip text for a condition — `Calls > 100`, `Calls 10–50` —
 * or `null` when the condition narrows nothing.
 */
// oxlint-disable-next-line react/only-export-components
export function numberConditionText(
  label: string,
  condition: ColumnNumberCondition | undefined,
): string | null {
  const test = resolveNumberCondition(condition);
  if (test === null) return null;
  if (test.op === 'between') {
    return `${label} ${formatConditionNumber(test.a)}–${formatConditionNumber(test.b)}`;
  }
  return `${label} ${NUMBER_OPERATOR_SYMBOLS[test.op]} ${formatConditionNumber(test.a)}`;
}

/** Is anything typed or chosen in the condition editor (even if unusable)? */
function conditionHasInput(condition: ColumnNumberCondition | undefined): boolean {
  if (!condition) return false;
  return condition.operator !== EMPTY_CONDITION.operator || condition.a !== '' || condition.b !== '';
}

/**
 * Validates a stored condition (filter memory is not this build's to trust).
 * Junk → `undefined`, i.e. no condition.
 */
// oxlint-disable-next-line react/only-export-components
export function parseStoredCondition(raw: unknown): ColumnNumberCondition | undefined {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const { operator, a, b } = raw as Record<string, unknown>;
  if (typeof operator !== 'string' || !(NUMBER_OPERATORS as readonly string[]).includes(operator)) {
    return undefined;
  }
  if (typeof a !== 'string' || typeof b !== 'string') return undefined;
  return { operator: operator as ColumnNumberOperator, a, b };
}

/*
 * The shared "nothing applied" state, beside the type it describes.
 *
 * Every consumer treats `ColumnFilterState` as immutable and replaces it
 * wholesale, so one shared instance is safe and keeps identity checks cheap.
 */
// oxlint-disable-next-line react/only-export-components
export const EMPTY_FILTER: ColumnFilterState = { selected: [], emptiness: 'any' };

/**
 * The "no bounds yet" range, and the operator a numeric popover opens on.
 *
 * `atLeast` rather than `between`, because it is the one operator that asks a
 * complete question from a single box — opening on `between` would present two
 * empty boxes, neither of which does anything alone.
 */
// oxlint-disable-next-line react/only-export-components
export const EMPTY_RANGE: ColumnRangeValue = { operator: 'atLeast', min: '', max: '' };

export interface ColumnFilterProps {
  label: string;
  options: ColumnFilterOption[];
  value: ColumnFilterState;
  onChange: (next: ColumnFilterState) => void;
  /** Omit entirely when the API cannot sort this column — then NO sort UI renders. */
  sort?: {
    direction: SortDirection | null;
    onSort: (direction: SortDirection) => void;
  };
  scope: FilterScope;
  /** Rows currently loaded — used for the page-scope honesty note. */
  pageRowCount?: number;
  /** True while the option list is still loading. */
  loading?: boolean;
  disabled?: boolean;
  /**
   * Overrides the computed scope footnote.
   *
   * `useTableFilters` uses it for the one case the `scope` flag alone cannot
   * describe: a server-backed column with **several** values ticked. Those API
   * params take one value each (`?ownerId=3,7` coerces to NaN and 422s), so the
   * hook sends the param only when exactly one is chosen and filters the loaded
   * page itself otherwise — and the footnote has to say which is happening.
   */
  scopeNote?: string;
  /**
   * Present = this is a NUMERIC column: the popover offers comparison operators
   * over a min/max pair instead of a list of tickable values.
   *
   * ⚠️ **`options` is then ignored**, and so are the value filter box and the
   * select-all row. A number column's useful question is "above X", which a
   * list of the distinct figures that happen to be on the loaded page cannot
   * ask — there were 800 of them and the one you want is on another page.
   *
   * ⚠️ **Is empty / Is not empty stay, and they are the reason the operator set
   * is honest.** A null is not a zero anywhere in this app (an unpriced product
   * against a free one, an uncapped project against a sold-out one), and the
   * bounds are INCLUSIVE with a NULL matching NEITHER — so `At least 0` is not
   * "everything" and the min/max pair does not partition the list. The only way
   * to reach an unrecorded figure is Is empty, which is why dropping those two
   * boxes here would remove a capability rather than tidy one.
   */
  range?: ColumnRangeSpec;
  /**
   * `'number'` = a plain-number column tested on the client (owner 2026-10-29):
   * the panel adds a condition editor (>, ≥, <, ≤, =, Between) ABOVE the value
   * list, which stays, as in LAE. Ignored when `range` is present. Never for
   * money: see `ColumnNumberOperator`.
   */
  kind?: 'number';
}

/** Height of the scrolling value list. The rest of the popover sits above it. */
const VALUE_LIST_MAX_HEIGHT = 200;
/** Rough total height, used only to decide whether to flip above the trigger. */
const POPOVER_EST_HEIGHT = 360;
const POPOVER_WIDTH = 288;

function FunnelIcon({ active }: { active: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      /* Filled when the column is filtered: a shape change, not a colour change,
       * because colour alone is never the only signal in this system. */
      fill={active ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-3.5 w-3.5 shrink-0"
    >
      <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3Z" />
    </svg>
  );
}

function ArrowUpIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-3.5 w-3.5 shrink-0"
    >
      <path d="M12 19V5M5 12l7-7 7 7" />
    </svg>
  );
}

function ArrowDownIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-3.5 w-3.5 shrink-0"
    >
      <path d="M12 5v14M5 12l7 7 7-7" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-4 w-4 shrink-0 text-fg-subtle"
    >
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </svg>
  );
}

function plural(count: number, one: string, many: string) {
  return `${count} ${count === 1 ? one : many}`;
}

/** The operator buttons, in the order they are offered. */
const RANGE_OPERATORS: readonly ColumnRangeOperator[] = ['atLeast', 'atMost', 'equals', 'between'];

/**
 * ⚠️ **These four words ARE the inclusive-bound contract**, and they are the
 * only place it is stated — see `ColumnRangeOperator`. "At least 5" includes 5
 * in plain English and `?amountMin=5` includes 5 on the wire; "Greater than 5"
 * would claim otherwise about the one row that matters. **Do not reword these to
 * a strict comparison** without changing what goes out, which cannot be done.
 */
const RANGE_OPERATOR_LABELS: Record<ColumnRangeOperator, string> = {
  atLeast: 'At least',
  atMost: 'At most',
  equals: 'Equals',
  between: 'Between',
};

/**
 * The same four operators over a DATE column. Same inclusive contract — "On or
 * after 1 March" includes 1 March, exactly as `?…After=` does on the wire.
 */
const DATE_RANGE_OPERATOR_LABELS: Record<ColumnRangeOperator, string> = {
  atLeast: 'On or after',
  atMost: 'On or before',
  equals: 'On',
  between: 'Between',
};

/** Which of the two bounds this operator renders — and therefore sends. */
function rangeSlots(operator: ColumnRangeOperator): { min: boolean; max: boolean } {
  return { min: operator !== 'atMost', max: operator === 'between' };
}

/*
 * Shapes, not values. 12 integer digits covers `DECIMAL(14,2)`'s whole part and
 * an `INT` column alike without refusing anything real; two decimal places is
 * every money scale in this schema.
 *
 * The sign is tested separately so the refusal can say which rule was broken —
 * "cannot be negative" is actionable where "enter a number" over `-5` is not.
 */
const BOUND_INTEGER = /^-?\d{1,12}$/;
const BOUND_DECIMAL = /^-?\d{1,12}(\.\d{1,2})?$/;

/**
 * A refusal for one bound box, or `null` when it is empty or usable.
 *
 * ⚠️ **An empty box is NOT an error** — it is the ordinary state of the bound
 * the person is not using, and the one thing that must never reach the wire.
 *
 * ⛔ **There is deliberately NO min-greater-than-max check here, and one must
 * not be added.** Nothing on the server refuses that window, and a range with
 * no rows in it is an empty RESULT rather than a malformed request — the same
 * ruling `/projects` takes on a project running outside its cycle's dates. A
 * client-side refusal there would be a frontend convention dressed as an API
 * rule, and it would sit on a control whose two numbers are both on screen.
 */
function rangeBoundError(raw: string, spec: ColumnRangeSpec): string | null {
  const text = raw.trim();
  if (text === '') return null;
  // A date bound is a calendar day or nothing. `signed` and `decimal` mean
  // nothing here, so they are not consulted.
  if (spec.date === true) return isRealDate(text) ? null : 'Enter a real date.';
  if (text.startsWith('-') && spec.signed !== true) return 'Cannot be negative.';
  const pattern = spec.decimal === true ? BOUND_DECIMAL : BOUND_INTEGER;
  if (!pattern.test(text)) return spec.decimal === true ? 'Enter a number.' : 'Enter a whole number.';
  return null;
}

/**
 * The bounds this range should actually send, `null` for "omit the key".
 *
 * ⛔ **AN EMPTY OR UNUSABLE BOX IS OMITTED ENTIRELY, never sent blank.** On the
 * older pairs `?pendingMin=` coerces to a real bound of **zero** server-side — a
 * `200` with a silently narrowed list, and on `capacity` that would drop every
 * uncapped project, which is most of them. This function is the single gate
 * between a bound box and the query string, which is why the check lives here
 * and not at a call site.
 *
 * ⚠️ **The value is passed through as the STRING it was typed as.** No parse, no
 * reformat, no `toFixed` — a money bound is an exact decimal.
 */
// oxlint-disable-next-line react/only-export-components
export function rangeBounds(
  range: ColumnRangeValue | undefined,
  spec: ColumnRangeSpec,
): { min: string | null; max: string | null } {
  if (!range) return { min: null, max: null };
  const slots = rangeSlots(range.operator);
  const sendable = (raw: string) => {
    const text = raw.trim();
    if (text === '') return null;
    return rangeBoundError(text, spec) === null ? text : null;
  };
  const min = slots.min ? sendable(range.min) : null;
  // One typed value, both bounds — an inclusive pair at the same figure IS
  // equality, so `equals` needs no operator of its own on the wire.
  if (range.operator === 'equals') return { min, max: min };
  return { min, max: slots.max ? sendable(range.max) : null };
}

/**
 * Is a bound typed into a box this operator actually uses?
 *
 * Slot-gated on purpose: a `max` left over from an earlier `between` is not
 * "filtering" while the operator reads `atLeast`, because it is not being sent.
 * **Active means something went out** (or was attempted), which is what keeps
 * the funnel, the count and Clear all honest.
 */
function rangeIsSet(range: ColumnRangeValue | undefined): boolean {
  if (!range) return false;
  const slots = rangeSlots(range.operator);
  return (slots.min && range.min.trim() !== '') || (slots.max && range.max.trim() !== '');
}

/** The range as a short clause for an accessible name, or `null`. */
function rangeSummary(range: ColumnRangeValue | undefined, isDate = false): string | null {
  if (!rangeIsSet(range) || !range) return null;
  const slots = rangeSlots(range.operator);
  const min = slots.min ? range.min.trim() : '';
  const max = slots.max ? range.max.trim() : '';
  if (range.operator === 'equals') return isDate ? `on ${min}` : `equals ${min}`;
  if (min !== '' && max !== '') return `between ${min} and ${max}`;
  // A half-filled `between` sends one bound, so it is described as the one
  // bound it sends rather than as a window it is not asking for.
  if (isDate) return min !== '' ? `on or after ${min}` : `on or before ${max}`;
  return min !== '' ? `at least ${min}` : `at most ${max}`;
}

/**
 * True when this column's state would count towards `activeCount` — i.e. when
 * it is narrowing something.
 *
 * ⚠️ **Exported so `useTableFilters` cannot grow a second copy.** It had one
 * inline, twice over, before the range arrived; three spellings of "is this
 * filtered" would have disagreed about a numeric column on the first edit, and
 * the symptom is a filled funnel over an unfiltered table.
 */
// oxlint-disable-next-line react/only-export-components
export function isFilterActive(value: ColumnFilterState) {
  return (
    value.selected.length > 0 ||
    value.emptiness !== 'any' ||
    rangeIsSet(value.range) ||
    resolveNumberCondition(value.condition) !== null
  );
}

/**
 * True when the reader has put something in this panel that must survive being
 * stored — a WIDER test than `isFilterActive`, and the difference is the point.
 *
 * A chosen operator with no bound yet filters nothing, so it must not fill the
 * funnel or be counted. It must still be REMEMBERED: dropping the entry the
 * moment both boxes are empty would snap the operator back to `atLeast` under
 * somebody who had just picked `At most` and was about to type into it.
 */
// oxlint-disable-next-line react/only-export-components
export function hasFilterInput(value: ColumnFilterState) {
  if (isFilterActive(value)) return true;
  // A chosen operator or a half-typed box is kept, for the reason above.
  if (conditionHasInput(value.condition)) return true;
  const range = value.range;
  if (!range) return false;
  // Any typed text counts here, in a slot this operator uses or not: Clear all
  // has to be able to empty a box the reader can see.
  return range.operator !== EMPTY_RANGE.operator || range.min !== '' || range.max !== '';
}

/**
 * The body of a column filter, with no shell of its own.
 *
 * Extracted so the same controls — and the same state — can appear in two
 * places: inside `ColumnFilter`'s portalled popover on a table header, and
 * inside `FilterSheet` on the card lists that replace those tables below the
 * `lg`/`md` breakpoint. Before this existed, every page that swapped its table
 * for cards lost its filters entirely at 320px.
 *
 * It renders a **fragment**, not a wrapper: the popover is a flex column whose
 * proportions depend on these blocks being its direct children, and an extra
 * div between them would change the desktop layout. Whatever hosts it supplies
 * the border, radius and background.
 *
 * Structure, top to bottom: sort (only when the API can sort this column),
 * is-empty / is-not-empty, a value filter box, and the distinct values.
 *
 * **A column that passes `range` takes a different second half**: the four
 * comparison operators and one or two bound boxes, in place of the filter box,
 * the select-all row and the value list. The first block is untouched — the sort
 * stays exactly where it was, and so do the two emptiness boxes, which on a
 * numeric column are the only way to reach an unrecorded figure.
 *
 * **Sort controls render only when `sort` is passed**, and that is load-bearing
 * rather than stylistic: most columns here cannot be sorted by the API at all
 * (the whole catalog module, `/users`), and unknown query params are silently
 * stripped — so a sort arrow that did nothing would look exactly like one that
 * worked.
 *
 * ⛔ **A scope footnote used to close the panel and was removed on 2026-09-05**
 * — see the block comment in the body. It used to be described here as "not
 * optional", on the grounds that a page-scoped filter touching 20 of several
 * thousand rows has to say so. **That reasoning was not refuted; it was
 * outranked by the owner's helper-text rule.**
 */
export function ColumnFilterPanel({
  label,
  options,
  value,
  onChange,
  sort,
  loading = false,
  range,
  kind,
}: ColumnFilterProps): React.JSX.Element {
  const [term, setTerm] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const baseId = useId();
  const listId = `${baseId}-values`;
  const listRef = useRef<HTMLUListElement>(null);

  const selectedCount = value.selected.length;

  const visible = useMemo(() => {
    const needle = term.trim().toLowerCase();
    if (needle === '') return options;
    return options.filter((option) => option.label.toLowerCase().includes(needle));
  }, [options, term]);

  // Clamped during render rather than reset in an effect: typing in the filter
  // box can leave the stored index past the end of the list.
  const active = visible.length === 0 ? 0 : Math.min(activeIndex, visible.length - 1);

  function valueInputs(): HTMLInputElement[] {
    const nodes = listRef.current?.querySelectorAll<HTMLInputElement>('input[type="checkbox"]');
    return nodes ? Array.from(nodes) : [];
  }

  function focusValue(index: number) {
    const inputs = valueInputs();
    const next = inputs[index];
    if (!next) return;
    setActiveIndex(index);
    next.focus();
  }

  // Always multi-select, on every column — see `scopeNote` above for why a
  // single-value server param doesn't limit this.
  function toggle(optionValue: string) {
    onChange({
      ...value,
      selected: value.selected.includes(optionValue)
        ? value.selected.filter((current) => current !== optionValue)
        : [...value.selected, optionValue],
    });
  }

  function setEmptiness(next: Emptiness) {
    // A single field, so the two boxes are mutually exclusive by construction:
    // ticking one cannot leave the other stuck on.
    onChange({ ...value, emptiness: next });
  }

  /*
   * The bounds are held as typed text and replaced wholesale, like every other
   * part of this state. Nothing here parses, rounds or reformats a bound: a
   * money bound is an exact decimal string and the wire wants it verbatim.
   */
  const rangeValue = value.range ?? EMPTY_RANGE;
  const rangeSlotsInUse = rangeSlots(rangeValue.operator);
  const rangeSpec = range ?? {};
  const minError = rangeBoundError(rangeValue.min, rangeSpec);
  const maxError = rangeBoundError(rangeValue.max, rangeSpec);

  /*
   * ⛔ **A SIGNED COLUMN GETS NO NUMERIC KEYPAD, AND THAT IS THE POINT.**
   * `inputMode="numeric"` and `inputMode="decimal"` both produce an iOS keypad
   * with **no minus key** — so on a signed column they would make a negative
   * bound literally untypeable on a phone, which is the exact failure `signed`
   * exists to prevent: `pending` is signed because overdelivery is real, and a
   * reader hunting overdelivered rows is the one person who needs to type a `-`.
   * Falling back to the ordinary keyboard is the only option that has one.
   */
  const isDateRange = rangeSpec.date === true;
  const boundInputMode = isDateRange
    ? undefined
    : rangeSpec.signed === true
      ? undefined
      : rangeSpec.decimal === true
        ? 'decimal'
        : 'numeric';
  const operatorLabels = isDateRange ? DATE_RANGE_OPERATOR_LABELS : RANGE_OPERATOR_LABELS;
  // A date box is the browser's own date control; a number box stays text, for
  // the reason given at the input below.
  const boundInputType = isDateRange ? 'date' : 'text';
  const boundInputClass = isDateRange ? 'tabular-nums' : 'text-right tabular-nums';

  function setRange(next: ColumnRangeValue) {
    onChange({ ...value, range: next });
  }

  /*
   * The number condition (owner 2026-10-29). Only on a `kind: 'number'` column
   * without a `range` — a range column's bounds are the server's, and offering
   * a client test beside them would be two filters that disagree at the boundary.
   */
  const showCondition = kind === 'number' && !range;
  const conditionValue = value.condition ?? EMPTY_CONDITION;
  const conditionAError = conditionBox(conditionValue.a) === undefined ? 'Enter a number.' : null;
  const conditionBError =
    conditionValue.operator === 'between' && conditionBox(conditionValue.b) === undefined
      ? 'Enter a number.'
      : null;
  const conditionTouched = conditionHasInput(value.condition);

  function setCondition(next: ColumnNumberCondition) {
    onChange({ ...value, condition: next });
  }

  function clearCondition() {
    // Rebuilt rather than spread-and-delete, so the stored state carries no
    // `condition` key at all once it is cleared.
    const next: ColumnFilterState = { selected: value.selected, emptiness: value.emptiness };
    if (value.range) next.range = value.range;
    onChange(next);
  }

  function selectAllVisible() {
    const merged = new Set(value.selected);
    for (const option of visible) merged.add(option.value);
    onChange({ ...value, selected: Array.from(merged) });
  }

  function clearVisible() {
    const drop = new Set(visible.map((option) => option.value));
    onChange({ ...value, selected: value.selected.filter((current) => !drop.has(current)) });
  }

  function onListKeyDown(event: React.KeyboardEvent) {
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    if (visible.length === 0) return;
    event.preventDefault();
    if (event.key === 'Home') return focusValue(0);
    if (event.key === 'End') return focusValue(visible.length - 1);
    const delta = event.key === 'ArrowDown' ? 1 : -1;
    focusValue((active + delta + visible.length) % visible.length);
  }

  const allVisibleSelected =
    visible.length > 0 && visible.every((option) => value.selected.includes(option.value));
  const anyVisibleSelected = visible.some((option) => value.selected.includes(option.value));

  /*
   * ⛔ **THE SCOPE FOOTNOTE WAS REMOVED — owner's helper-text ruling,
   * 2026-09-05.** It was the last thing in the popover and read one of:
   *
   * - `Filters the {N rows} on this page — not the whole table.`
   * - `Values re-query the whole table. Is empty / Is not empty apply only to
   *   the {N rows} loaded here.`
   * - whatever `scopeNote` overrode it with.
   *
   * ⚠️ **THE TWO SCOPES STILL BEHAVE DIFFERENTLY AND NOW LOOK IDENTICAL.** A
   * `page` filter narrows only what is loaded, so an empty result means "not on
   * this page" and NOT "not in the table" — the reader can no longer tell those
   * apart, and there is nothing on screen that distinguishes them.
   *
   * ⚠️ **`scope`, `pageRowCount` and `scopeNote` are all still ACCEPTED and are
   * deliberately unread here.** `useTableFilters` and three pages still compute
   * and pass them, including the case the `scope` flag alone cannot describe — a
   * server-backed column with SEVERAL values ticked, where the API param takes
   * one value each (`?ownerId=3,7` coerces to NaN and 422s) so the hook sends the
   * param only when exactly one is chosen and filters the loaded page itself
   * otherwise. **Do not delete the props to chase the dead render**: that is a
   * change to every caller, and restoring the note later would then be a contract
   * change rather than a render.
   */

  return (
    <>
      {/* Sort on the left, emptiness on the right — one block. */}
      <div className="flex shrink-0 items-start gap-3 border-b border-border p-2">
        {sort && (
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            {(['asc', 'desc'] as const).map((direction) => {
              const isActive = sort.direction === direction;
              return (
                <Button
                  key={direction}
                  variant="ghost"
                  size="sm"
                  aria-pressed={isActive}
                  onClick={() => sort.onSort(direction)}
                  className="w-full"
                  /*
                   * The box LEADS and the tick is gone — owner's instruction
                   * 2026-09-13. It is the same mark the value list below
                   * already uses, so the whole popover now says "chosen" one
                   * way instead of two.
                   *
                   * ⚠️ The direction ARROW stays and moves INTO the label,
                   * rather than being displaced by the box. It is not a
                   * selection mark at all — it is what tells Ascending from
                   * Descending at a glance, and dropping it to free the slot
                   * would trade a duplicate signal for a lost one.
                   */
                  leadingIcon={<RowCheckbox checked={isActive} />}
                >
                  {/* `flex-1 text-left` on the child rather than
                   * `justify-start` on the button, and the active weight
                   * on the child too: both would otherwise collide with a
                   * utility the primitive already sets for the same
                   * property, which does not resolve by class order. */}
                  {/*
                   * ⚠️ `truncate` goes on the TEXT span, not on this flex row.
                   * A bare text node inside a flex container is an anonymous
                   * flex item, and `text-overflow: ellipsis` on the container
                   * cannot reach it — the label would overflow instead of
                   * ellipsizing, which at 320px is how a word ends up painted
                   * over the box beside it. `min-w-0` for the usual reason: a
                   * flex item will not shrink below its content without it.
                   */}
                  <span className="flex flex-1 items-center gap-1.5 text-left">
                    {direction === 'asc' ? <ArrowUpIcon /> : <ArrowDownIcon />}
                    <span
                      className={cx('min-w-0 truncate', isActive && 'font-semibold text-fg')}
                    >
                      {direction === 'asc' ? 'Ascending' : 'Descending'}
                    </span>
                  </span>
                </Button>
              );
            })}
          </div>
        )}

        <div className="flex min-w-0 flex-1 flex-col gap-2 py-1">
          <Checkbox
            label="Is empty"
            checked={value.emptiness === 'empty'}
            onChange={(event) => setEmptiness(event.target.checked ? 'empty' : 'any')}
          />
          <Checkbox
            label="Is not empty"
            checked={value.emptiness === 'notEmpty'}
            onChange={(event) => setEmptiness(event.target.checked ? 'notEmpty' : 'any')}
          />
        </div>
      </div>

      {/*
        * The number condition, above the value list as in LAE. Six operators
        * in two rows, one or two boxes, and Clear. Unusable text shows an
        * error and filters nothing.
        */}
      {showCondition && (
        <div className="flex shrink-0 flex-col gap-2 border-b border-border p-2">
          <div role="group" aria-label={`${label} condition`} className="grid grid-cols-3 gap-0.5">
            {NUMBER_OPERATORS.map((operator) => {
              const isActive = conditionValue.operator === operator;
              return (
                <Button
                  key={operator}
                  variant="ghost"
                  size="sm"
                  aria-pressed={isActive}
                  aria-label={NUMBER_OPERATOR_NAMES[operator]}
                  onClick={() => setCondition({ ...conditionValue, operator })}
                  className="w-full"
                  leadingIcon={<RowCheckbox checked={isActive} />}
                >
                  <span
                    className={cx('flex-1 truncate text-left tabular-nums', isActive && 'font-semibold text-fg')}
                  >
                    {NUMBER_OPERATOR_SYMBOLS[operator]}
                  </span>
                </Button>
              );
            })}
          </div>

          <div className="flex items-start gap-2">
            <div className="min-w-0 flex-1">
              <Field
                label={
                  conditionValue.operator === 'between'
                    ? `${label} from`
                    : `${label} ${NUMBER_OPERATOR_NAMES[conditionValue.operator].toLowerCase()}`
                }
                hideLabel
                error={conditionAError}
              >
                {({ id, 'aria-describedby': describedBy }) => (
                  <Input
                    id={id}
                    aria-describedby={describedBy}
                    type="text"
                    inputMode="decimal"
                    invalid={conditionAError !== null}
                    value={conditionValue.a}
                    onChange={(event) => setCondition({ ...conditionValue, a: event.target.value })}
                    placeholder={conditionValue.operator === 'between' ? 'From' : 'Value'}
                    className="text-right tabular-nums"
                  />
                )}
              </Field>
            </div>

            {conditionValue.operator === 'between' && (
              <div className="min-w-0 flex-1">
                <Field label={`${label} to`} hideLabel error={conditionBError}>
                  {({ id, 'aria-describedby': describedBy }) => (
                    <Input
                      id={id}
                      aria-describedby={describedBy}
                      type="text"
                      inputMode="decimal"
                      invalid={conditionBError !== null}
                      value={conditionValue.b}
                      onChange={(event) => setCondition({ ...conditionValue, b: event.target.value })}
                      placeholder="To"
                      className="text-right tabular-nums"
                    />
                  )}
                </Field>
              </div>
            )}

            <Button
              variant="ghost"
              size="sm"
              disabled={!conditionTouched}
              onClick={clearCondition}
              aria-label={`Clear ${label} condition`}
              className="shrink-0"
            >
              Clear
            </Button>
          </div>
        </div>
      )}

      {/*
        * The numeric branch. `min-h-0 flex-1 overflow-y-auto` for the same
        * reason the value list carries them: on a 320×568 screen the popover is
        * capped to the room below its trigger, and without this the second bound
        * box is clipped off the bottom rather than scrolled to.
        */}
      {range && (
        <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto border-b border-border p-2">
          <div role="group" aria-label={`${label} comparison`} className="grid grid-cols-2 gap-0.5">
            {RANGE_OPERATORS.map((operator) => {
              const isActive = rangeValue.operator === operator;
              return (
                <Button
                  key={operator}
                  variant="ghost"
                  size="sm"
                  aria-pressed={isActive}
                  // Changing the operator never clears a bound — see
                  // `ColumnRangeValue`. It changes which ones are rendered, and
                  // therefore which ones are sent.
                  onClick={() => setRange({ ...rangeValue, operator })}
                  className="w-full"
                  // Leading box, no trailing tick — as the sort rows above and
                  // the value list below.
                  leadingIcon={<RowCheckbox checked={isActive} />}
                >
                  {/* Same construction as the sort buttons above, and for the
                   * same reason: the weight and the alignment go on a child,
                   * because either one on the Button would collide with a
                   * utility the primitive already sets for that property, and
                   * those do not resolve by class order. */}
                  <span
                    className={cx('flex-1 truncate text-left', isActive && 'font-semibold text-fg')}
                  >
                    {operatorLabels[operator]}
                  </span>
                </Button>
              );
            })}
          </div>

          <div className="flex items-start gap-2">
            {rangeSlotsInUse.min && (
              <div className="min-w-0 flex-1">
                <Field
                  label={rangeValue.operator === 'equals' ? `${label} equals` : `${label} from`}
                  hideLabel
                  error={minError}
                >
                  {({ id, 'aria-describedby': describedBy }) => (
                    <Input
                      id={id}
                      aria-describedby={describedBy}
                      /* ⚠️ `type="text"`, never `type="number"`. A number input
                       * hands back a value the browser has normalised and
                       * invites `valueAsNumber` at the next call site — and a
                       * money bound here is an exact decimal string that must
                       * reach the wire as typed. `inputMode` still gets the
                       * numeric keypad on a phone, which is the only thing
                       * `type="number"` was wanted for. */
                      type={boundInputType}
                      inputMode={boundInputMode}
                      invalid={minError !== null}
                      value={rangeValue.min}
                      onChange={(event) => setRange({ ...rangeValue, min: event.target.value })}
                      placeholder={rangeValue.operator === 'equals' ? 'Value' : 'From'}
                      className={boundInputClass}
                    />
                  )}
                </Field>
              </div>
            )}

            {rangeSlotsInUse.max && (
              <div className="min-w-0 flex-1">
                <Field label={`${label} to`} hideLabel error={maxError}>
                  {({ id, 'aria-describedby': describedBy }) => (
                    <Input
                      id={id}
                      aria-describedby={describedBy}
                      type={boundInputType}
                      inputMode={boundInputMode}
                      invalid={maxError !== null}
                      value={rangeValue.max}
                      onChange={(event) => setRange({ ...rangeValue, max: event.target.value })}
                      placeholder="To"
                      className={boundInputClass}
                    />
                  )}
                </Field>
              </div>
            )}
          </div>
        </div>
      )}

      {/*
        * The value list and everything that drives it. A numeric column has no
        * use for it: the question is "above X", and a list of the distinct
        * figures that happen to be on the loaded page cannot ask that — the
        * figure somebody wants is usually on another page.
        */}
      {!range && (
        <>
          <div className="shrink-0 border-b border-border p-2">
            <Input
              type="text"
              value={term}
              onChange={(event) => {
                setTerm(event.target.value);
                setActiveIndex(0);
              }}
              onKeyDown={(event) => {
                if (event.key !== 'ArrowDown') return;
                event.preventDefault();
                focusValue(0);
              }}
              placeholder="Filter values…"
              aria-label={`Filter ${label} values`}
              aria-controls={listId}
              leadingIcon={<SearchIcon />}
            />
          </div>

          <div className="flex shrink-0 items-center gap-1 border-b border-border px-1 py-0.5">
            {(
              <Button
                variant="ghost"
                size="sm"
                disabled={visible.length === 0 || allVisibleSelected}
                onClick={selectAllVisible}
              >
                Select all
              </Button>
            )}
            {/* Both act on the *visible* subset, so filtering to "Aus" and
             * hitting Clear cannot silently untick values you can't see. With
             * an empty filter box the visible subset is everything. */}
            <Button variant="ghost" size="sm" disabled={!anyVisibleSelected} onClick={clearVisible}>
              Clear
            </Button>
            {/* Live, because ticking a box changes nothing else a screen
             * reader would notice until the table behind it re-renders. */}
            <p role="status" className="ml-auto pr-1 text-2xs text-fg-muted">
              {selectedCount === 0 ? 'None selected' : `${selectedCount} selected`}
            </p>
          </div>

          <ul
            id={listId}
            ref={listRef}
            onKeyDown={onListKeyDown}
            style={{ maxHeight: VALUE_LIST_MAX_HEIGHT }}
            // min-h-0 so a short viewport shortens the list rather than
            // pushing the scope footnote off the bottom of the popover.
            className="m-0 min-h-0 flex-1 list-none overflow-y-auto p-1"
          >
            {loading && <li className="px-2 py-3 text-center text-xs text-fg-muted">Loading…</li>}

            {!loading && visible.length === 0 && (
              <li className="px-2 py-3 text-center text-xs text-fg-muted">
                {options.length === 0
                  ? 'No values on this page'
                  : `No values matching “${term.trim()}”`}
              </li>
            )}

            {!loading &&
              visible.map((option, index) => (
                <li
                  key={option.value}
                  className={cx(
                    'flex items-center gap-2 rounded-md px-1.5',
                    'transition-colors duration-150 ease-out motion-reduce:transition-none',
                    'hover:bg-surface-hover',
                  )}
                >
                  <Checkbox
                    className="min-w-0 flex-1 py-1.5"
                    checked={value.selected.includes(option.value)}
                    onChange={() => toggle(option.value)}
                    onFocus={() => setActiveIndex(index)}
                    // Roving tab stop: one entry in the popover's Tab cycle,
                    // arrow keys to move within the list.
                    tabIndex={index === active ? 0 : -1}
                    aria-label={
                      option.count === undefined
                        ? option.label
                        : `${option.label}, ${plural(option.count, 'row', 'rows')}`
                    }
                    label={<span className="block truncate">{option.label}</span>}
                  />
                  {option.count !== undefined && (
                    <span className="shrink-0 pr-1 text-2xs text-fg-muted tabular-nums">
                      {option.count}
                    </span>
                  )}
                </li>
              ))}
          </ul>
        </>
      )}
    </>
  );
}

/**
 * The Airtable-style column filter, told honestly.
 *
 * A funnel trigger in a table header, plus `ColumnFilterPanel` in a popover.
 * Everything here is the popover *shell* — the controls themselves live in the
 * panel, which `FilterSheet` reuses verbatim on narrow screens.
 *
 * Two things here are load-bearing rather than stylistic:
 *
 * - **The popover is portalled and positioned `fixed`.** It lives inside
 *   `TableWrap`, whose `overflow-x-auto` would otherwise clip it to the table's
 *   box — the same reason `Combobox` does it inside a scrolling modal.
 * - **Tab cycles inside the popover.** Portalled, it is nowhere near its
 *   trigger in the document order, so an uncaught Tab would strand focus at the
 *   end of the page.
 */
export function ColumnFilter(props: ColumnFilterProps): React.JSX.Element {
  const { label, value, sort, disabled = false } = props;

  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState<{
    left: number;
    top: number;
    width: number;
    maxHeight: number;
    flip: boolean;
  }>({
    left: 0,
    top: 0,
    width: POPOVER_WIDTH,
    maxHeight: POPOVER_EST_HEIGHT,
    flip: false,
  });

  const baseId = useId();
  const titleId = `${baseId}-title`;
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const selectedCount = value.selected.length;
  const isFiltered = isFilterActive(value);
  const isSorted = sort?.direction != null;

  const place = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const box = el.getBoundingClientRect();
    const width = Math.min(POPOVER_WIDTH, window.innerWidth - 16);
    const below = window.innerHeight - box.bottom;
    const flip = below < POPOVER_EST_HEIGHT && box.top > below;
    // Right-aligned to the trigger, then clamped into the viewport. These sit in
    // table headers, and the last column's header is hard against the edge — at
    // 320px a left-aligned popover would hang off the screen.
    const maxLeft = Math.max(8, window.innerWidth - width - 8);
    const left = Math.min(Math.max(box.right - width, 8), maxLeft);
    // Cap to the room actually available and let the value list absorb the
    // difference. Full height, this popover is ~460px with the touch bumps
    // applied — taller than a 320×568 phone can show below a table header.
    const maxHeight = Math.max(200, (flip ? box.top : window.innerHeight - box.bottom) - 12);
    setRect({ left, top: flip ? box.top : box.bottom, width, maxHeight, flip });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    place();
  }, [open, place]);

  // The table scrolls sideways under the popover and the page scrolls under
  // that, so follow the trigger rather than floating over unrelated columns.
  useEffect(() => {
    if (!open) return;
    const onMove = () => place();
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open, place]);

  // The filter box owns first focus. Queried from the popover rather than held
  // in a ref, because the input now belongs to the panel — and the panel must
  // not grab focus in `FilterSheet`, where a dozen of them are mounted at once.
  useEffect(() => {
    if (!open) return;
    popoverRef.current?.querySelector<HTMLInputElement>('input[type="text"]')?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent) {
      const target = event.target as Node;
      if (popoverRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      setOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  function close(returnFocus = true) {
    // The panel unmounts with the popover, so its filter term resets with it —
    // no explicit reset needed here.
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  }

  function onPopoverKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'Escape') {
      event.preventDefault();
      // Otherwise a popover opened inside a Modal closes the Modal too.
      event.stopPropagation();
      close();
      return;
    }
    if (event.key !== 'Tab') return;
    // Cycle Tab inside the popover (portalled, so not adjacent to its trigger
    // in tab order).
    const root = popoverRef.current;
    if (!root) return;
    const focusable = Array.from(
      root.querySelectorAll<HTMLElement>('button:not([disabled]), input:not([disabled])'),
    ).filter((el) => el.tabIndex !== -1);
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!first || !last) return;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /*
   * The accessible name carries the state, because the only visual difference
   * between a filtered and an unfiltered column is a small filled funnel.
   */
  const state: string[] = [];
  if (selectedCount > 0) state.push(`${plural(selectedCount, 'value', 'values')} selected`);
  // Reads out as the bounds behave — "at least 5", not "greater than 5" — for
  // the reason `ColumnRangeOperator` gives at length.
  const bounds = rangeSummary(value.range, props.range?.date === true);
  if (bounds !== null) state.push(bounds);
  // Reads e.g. "Calls > 100"; only a `kind: 'number'` column ever holds one.
  const conditionText = props.range ? null : numberConditionText(label, value.condition);
  if (conditionText !== null) state.push(conditionText);
  if (value.emptiness === 'empty') state.push('is empty');
  if (value.emptiness === 'notEmpty') state.push('is not empty');
  if (sort?.direction === 'asc') state.push('sorted ascending');
  if (sort?.direction === 'desc') state.push('sorted descending');
  const triggerLabel = state.length > 0 ? `${label} — ${state.join(', ')}` : `Filter ${label}`;

  return (
    <>
      <Button
        ref={triggerRef}
        variant="ghost"
        size="sm"
        iconOnly
        disabled={disabled}
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={triggerLabel}
        // The hover label names the action only; the state stays in the
        // accessible name above, where the filled funnel cannot carry it.
        tooltip={sort ? `Filter and sort ${label}` : `Filter ${label}`}
        // Negative margin, not a height override: the primitive's h-8 would
        // otherwise deepen every header row by half a line.
        className="-my-1 max-sm:-my-1.5"
        leadingIcon={
          /* The tint goes on a wrapper, not on the button: a colour utility
           * beside the ghost variant's `text-fg-muted` is two utilities for the
           * same property, and those do not resolve by class order. An explicit
           * colour on a child always beats an inherited one. Neutral ink, not
           * the accent (reserved for primary actions, active nav and focus):
           * the filled funnel is the signal, the darker ink a second one. */
          <span className={isFiltered || isSorted ? 'text-fg' : undefined}>
            <FunnelIcon active={isFiltered} />
          </span>
        }
      />

      {open &&
        createPortal(
          <div
            ref={popoverRef}
            role="dialog"
            aria-labelledby={titleId}
            onKeyDown={onPopoverKeyDown}
            style={{
              position: 'fixed',
              left: rect.left,
              width: rect.width,
              maxHeight: rect.maxHeight,
              ...(rect.flip ? { bottom: window.innerHeight - rect.top + 4 } : { top: rect.top + 4 }),
            }}
            className="z-[60] flex flex-col overflow-hidden rounded-lg border border-border bg-surface-raised shadow-md"
          >
            <p id={titleId} className="sr-only">
              {label} — filter{sort ? ' and sort' : ''}
            </p>

            <ColumnFilterPanel {...props} />
          </div>,
          document.body,
        )}
    </>
  );
}

/** A `TH` with the funnel affordance built in. Omit `filter` for a plain header. */
export interface FilterableTHProps {
  label: string;
  numeric?: boolean;
  className?: string;
  /** Pins the column to the right edge of the scroller — see the note in `Table`. */
  pinned?: boolean;
  /**
   * Let a long heading run onto a second line instead of truncating.
   *
   * 🔴 **Added 2026-09-12 for the University Partnerships cards, and the default
   * is unchanged so no existing header moves.** Those cards are `table-fixed`
   * with a `<colgroup>`, so a column cannot grow to fit its heading — and their
   * headings are long enough that the choice is real: `Awaiting signature` over
   * a ~7rem column truncates to about `Awaiting s…`, which names nothing. That
   * page had already rejected truncation once, at the `SortHeader` it is
   * replacing, after a plain `whitespace-nowrap` heading OVERFLOWED INTO THE
   * NEXT COLUMN and the two read as one run-on word (reported 2026-09-03).
   *
   * ⚠️ **Wrapping and truncating are both fine; NOWRAP is the one that breaks
   * silently** under `table-fixed`, because an over-wide heading there neither
   * truncates nor widens its column. This prop chooses between the two safe
   * options, and neither branch can produce the third.
   */
  wrap?: boolean;
  /**
   * Freezes this column to an edge — forwarded straight to `TH`.
   *
   * 🔴 **Added 2026-09-12, and it is what stops every adopting page hand-rolling
   * its own header cell.** Without it a page that wants a frozen column cannot
   * use `FilterableTH` at all: it has to render `TH` + `ColumnFilter` itself and
   * then keep that copy in step with this one for ever. The first adoption did
   * exactly that before this prop existed, and reported it.
   */
  frozen?: FrozenSide;
  /** px from that edge. Measured — see `useFrozenOffsets`. */
  frozenOffset?: number;
  filter?: ColumnFilterProps;
}

export function FilterableTH({
  label,
  numeric,
  pinned,
  className,
  wrap,
  frozen,
  frozenOffset,
  filter,
}: FilterableTHProps) {
  if (!filter) {
    return (
      <TH
        numeric={numeric}
        pinned={pinned}
        frozen={frozen}
        frozenOffset={frozenOffset}
        className={className}
        wrap={wrap}
      >
        {label}
      </TH>
    );
  }

  // Only claim a sort state when the API can actually sort the column —
  // `aria-sort="none"` on an unsortable header promises a control that is
  // deliberately not rendered.
  const ariaSort = filter.sort
    ? filter.sort.direction === 'asc'
      ? 'ascending'
      : filter.sort.direction === 'desc'
        ? 'descending'
        : 'none'
    : undefined;

  return (
    <TH
      numeric={numeric}
      pinned={pinned}
      frozen={frozen}
      frozenOffset={frozenOffset}
      className={className}
      wrap={wrap}
      aria-sort={ariaSort}
    >
      {/* inline-flex inside the cell, so `numeric`'s text-right still parks the
       * whole label-plus-funnel group on the right edge. */}
      <span className="inline-flex items-center gap-1 align-middle">
        {/* ⚠️ **The default branch is BYTE-IDENTICAL to what it was** — no
          * `min-w-0` added to it. That would have been an improvement in theory
          * (a flex child will not shrink below its content width, so `truncate`
          * alone under-truncates) and a silent change to every header on every
          * list page, which is not this change's to make. The `wrap` branch
          * needs it because a wrapping label must be allowed to narrow, or the
          * funnel is pushed out of a `table-fixed` cell and becomes
          * unclickable. */}
        <span className={wrap ? 'min-w-0' : 'truncate'}>{label}</span>
        <ColumnFilter {...filter} />
      </span>
    </TH>
  );
}

export interface FilterSheetProps {
  /** Column keys in table order. */
  keys: readonly string[];
  /** From useTableFilters — the same function the headers use. */
  headerFor: (key: string) => FilterableTHProps;
  activeCount: number;
  clearAll: () => void;
  className?: string;
}

/**
 * Every column filter on one screen, for the widths that have no table.
 *
 * `DeliverablesPage`, `DealsPage`, `CompaniesPage` and `ContactsPage` all swap
 * their table for a card list below `lg`/`md`, and cards have no headers — so
 * at 320px those pages had **zero** filter controls. This is the way back in.
 *
 * It is the *same state*, not a parallel one: sections are driven by the very
 * `headerFor(key)` the headers spread, so a filter set on a phone is still set
 * after rotating to a desktop width, and vice versa. There is no second store
 * to keep in sync.
 *
 * Visibility is the caller's business — pass `className="lg:hidden"` (or
 * `md:hidden`) to match whichever breakpoint that page drops its table at.
 * Returns `null` when no key has a filter at all, so a page whose columns are
 * all unfilterable shows nothing rather than an empty dialog.
 */
export function FilterSheet({
  keys,
  headerFor,
  activeCount,
  clearAll,
  className,
}: FilterSheetProps): React.JSX.Element | null {
  const [open, setOpen] = useState(false);
  const baseId = useId();

  // `headerFor` is a render-time lookup, so this is just a projection — no
  // memo, for the same reason the hook doesn't memoise `headerFor` itself.
  const sections: { key: string; label: string; filter: ColumnFilterProps }[] = [];
  for (const key of keys) {
    const header = headerFor(key);
    // A column with no `filter` (an actions column, a plain label) has nothing
    // to show — skipped rather than rendered as an empty section.
    if (!header.filter) continue;
    sections.push({ key, label: header.label, filter: header.filter });
  }

  if (sections.length === 0) return null;

  return (
    <>
      <Button
        variant="secondary"
        className={className}
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={open}
        // The visible label is just "Filters"; the count beside it is decorative
        // markup, so the state has to be said here as well.
        aria-label={
          activeCount > 0
            ? `Filters — ${plural(activeCount, 'column', 'columns')} filtered`
            : 'Filters'
        }
        leadingIcon={<FunnelIcon active={activeCount > 0} />}
      >
        Filters
        {activeCount > 0 && (
          /* A number, not a dot: the count is the signal, and the tint is only
           * reinforcement. The funnel fills at the same time. */
          <span
            aria-hidden="true"
            className="grid h-4 min-w-4 place-items-center rounded-full bg-fg-secondary px-1 text-2xs font-semibold text-fg-inverse tabular-nums"
          >
            {activeCount}
          </span>
        )}
      </Button>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Filters"
        /*
         * ⛔ **The `description` was removed — owner's helper-text ruling,
         * 2026-09-05.** It summarised the state as `{N} columns filtered` or
         * `No filters applied`.
         *
         * The count is not lost: the trigger button behind this sheet still
         * carries it as a badge and in its `aria-label`, and "Clear all filters"
         * below still appears only when there is something to clear.
         */
        size="sm"
        footer={
          <>
            {/* Only offered when there is something to clear, so the control
             * never sits there inert. `clearAll` leaves sorting alone — see the
             * note on it in `useTableFilters`. */}
            {activeCount > 0 && (
              <Button variant="secondary" onClick={clearAll}>
                Clear all filters
              </Button>
            )}
            <Button variant="primary" onClick={() => setOpen(false)}>
              Done
            </Button>
          </>
        }
      >
        {/* The dialog body scrolls; the footer is outside it, so neither Done
         * nor Clear all can be pushed off a 568px-tall screen no matter how
         * many columns a page has. */}
        <div className="flex flex-col gap-3">
          {sections.map(({ key, label, filter }) => {
            const headingId = `${baseId}-${key}`;
            return (
              /* A named group rather than a bare stack: the panel's own controls
               * ("Is empty", "Ascending") read identically in every section, so
               * the column name has to come from the container. */
              <div
                key={key}
                role="group"
                aria-labelledby={headingId}
                // Plain block, not a flex column: the panel's value list is
                // `flex-1` for the popover's benefit, and here it should simply
                // take its own height under its 200px cap.
                className="overflow-hidden rounded-lg border border-border bg-surface-raised"
              >
                <div className="flex items-center gap-2 border-b border-border bg-surface-sunken px-2.5 py-1.5">
                  <h3
                    id={headingId}
                    className="min-w-0 flex-1 truncate text-xs font-semibold text-fg"
                  >
                    {label}
                  </h3>
                  {isFilterActive(filter.value) && <Badge tone="neutral">Filtered</Badge>}
                </div>

                <ColumnFilterPanel {...filter} />
              </div>
            );
          })}
        </div>
      </Modal>
    </>
  );
}
